import i18next from 'i18next'
import { queryClient } from '@/shared/lib/queryClientSingleton'
import { queryKeys } from '@/api/queryKeys'
import { resolveLocale } from '@/i18n/supportedLocales'

/*
 * WHICH ZONE A DATE IS SHOWN IN (RB-08 Fix 3). Two kinds of instant, two answers:
 *
 *   SCHEDULING — calendar entries, shoots, booking requests and slots, inquiry windows:
 *     "when must the client and the photographer be somewhere". Rendered in the STUDIO's
 *     zone, `StudioSettings.calendarTimezone`, passed as `{ timeZone }`. The shoot happens
 *     where the studio is; a member on a laptop still set to London must read 14:00, not
 *     13:00, for a 14:00 Zürich session — and a 23:30 Zürich shoot must not move to the
 *     previous day. Every such screen names the zone on screen (`StudioTimeZoneNote`).
 *   EVERYTHING ELSE — invoice and due dates, task due dates, gallery dates, activity and
 *     audit stamps: "when did this happen". Rendered in the VIEWER's zone: omit `timeZone`.
 *
 * Only the zone moves between the two. The SHAPE (locale, CH numeric rule, the member's
 * date-format preference) is the same either way.
 */

interface CachedSettings {
  locale?: string | null
}

/**
 * The team member's active UI LANGUAGE, as a formattable locale tag.
 *
 * <b>Not the studio-side formatting locale — see `getStudioFormatLocale()` below.</b> Since
 * 2026-09-16 this answers one question only: what language is this reader reading? Every
 * shaped output (money, numbers, dates, clock times) asks the studio locale instead, so
 * that a figure and the date next to it can never come from two regions. What is left here
 * is text: `formatRelativeTime`'s CLDR phrases, and the plain modules that need the tag to
 * build a *worded* Intl format of their own.
 *
 * It also remains the FALLBACK inside `getStudioFormatLocale()` for the paint before
 * /settings resolves, and it is what makes the public client-facing pages work: there is no
 * studio-settings query there, and `usePublicLocale` has put the client's own locale on the
 * i18next singleton by then.
 *
 * Imported from `i18next` rather than `@/i18n` deliberately: it is the same
 * singleton (our `@/i18n` module configures that instance), but importing the
 * configured module would run `i18n.init()` — the detector, the lazy backend and
 * seven JSON bundles — inside every unit test that touches a formatter. Before init
 * `i18next.language` is undefined and `resolveLocale` yields 'en-US', which is the
 * right pre-login default anyway.
 *
 * CAVEAT — this is a BUNDLE locale, one of the seven, and therefore carries no meaningful
 * region AT ALL. That is now a feature rather than the caveat it used to be: a region is
 * exactly what this function must not be asked for. `resolveLocale` collapses `de-DE` →
 * `de-CH`, `pt-PT` → `pt-BR` and `en-CH` → `en-US`, so reading a REGION off the result is
 * reading a default, not a fact. Take `getStudioFormatLocale()` for studio-side shape and
 * the client's own tag from the payload for anything rendering FOR a client (see the
 * `locale` override on `formatDate` below, and `makeMoneyFormatter` on the public offer /
 * invoice pages).
 *
 * EXPORTED for the few call sites that need the LANGUAGE tag rather than a formatted
 * string — a plain module with no hook, so no `useTranslation()`, building its own worded
 * Intl format. Reach for a formatter first; take the tag only when none of them fits.
 */
export function getActiveUiLocale(): string {
  return resolveLocale(i18next.language)
}

/**
 * THE locale that formats studio-side output: `StudioSettings.locale`, read from the
 * React Query cache.
 *
 * <b>One source for money AND dates AND numbers, deliberately.</b> Until 2026-09-16 money
 * formatted on the studio locale while every date and number formatted on the reader's UI
 * locale, so the two halves of one table row could disagree: a studio on `en-US` with a
 * `de-CH` member rendered `CHF 3,200.00` beside `15. Sept. 2026` — American grouping next
 * to a Swiss date, on the same line, with nothing in the app to explain it. Andras settled
 * it on 2026-09-16: studio-side dates and money use the same source, and that source is
 * the studio locale.
 *
 * The model this lands on is the ordinary one — the same split Windows, macOS and every
 * accounting package make: **region decides FORMAT, the member's own setting decides
 * LANGUAGE.** A studio states its region once (Settings → Studio profile → Client
 * language), and every figure the team reads is shaped by it, whoever is reading. Which is
 * exactly why `en-CH` had to exist first: before it, a Swiss studio working in English had
 * no way to say "English words, Swiss numbers", and unifying onto `en-US` would have made
 * the mismatch worse rather than better.
 *
 * What still follows the READER and not this: the language of the chrome around the
 * figures, and `formatRelativeTime`'s phrases, which are sentences rather than formats.
 * Month and weekday NAMES do come from here — they are part of a date's shape, and
 * splitting "the 15th" from "September" across two locales is the very defect above.
 *
 * Andras restated it on 2026-10-06 (D57), after RB-28 item 5 had briefly narrowed it so that a
 * reader in another language got their own shapes: *"Studio's setting should win in terms of
 * number and date formats, the viewers language should translate the words."* So the rule is
 * exactly this and has no exception: **the studio's locale governs every date, number, time and
 * money format for every reader; the reader's UI language governs only the words.** A Hungarian
 * team member in an en-US studio reads Hungarian labels around "Oct 5, 2026" and "CHF 1,079.92";
 * a de-CH studio read in English shows "05.10.2026" and "CHF 1’079.92". Narrowing it by language
 * would put two shapes on one screen as soon as a page mixes a studio figure with a figure the
 * reader's own setting shaped — the very defect above.
 *
 * The fallback is the reader's UI locale, not 'en-US': the cache is empty on first paint,
 * and grouping an amount as "1,234.50" before the settings request resolves and then
 * reflowing it to "1'234.50" is a visible flicker on every money-bearing page load. It is
 * also what makes the public client-facing pages correct for free — there is no studio
 * settings query there, and `usePublicLocale` has already put the CLIENT's locale on the
 * i18next singleton.
 */
export function getStudioFormatLocale(): string {
  const settings = queryClient.getQueryData(queryKeys.settings.all) as CachedSettings | undefined
  return settings?.locale ?? getActiveUiLocale()
}

/**
 * Swiss grouping, agreeing with the backend (G4-R2).
 *
 * The ICU data bundled with the runtimes this frontend ships to (Node here, browsers in
 * production) groups a de-CH/en-CH number with U+0027 APOSTROPHE — `toLocaleString('de-CH', …)`
 * really does return `"1'234.56"` natively, nothing in this codebase forces it. The JVM's own
 * CLDR data behind the backend (`MoneyFormatter`, invoice/offer PDFs, QR-bill and client
 * emails) groups the SAME amount with U+2019 RIGHT SINGLE QUOTATION MARK — the character CLDR
 * itself specifies for this position, and the one every Swiss bank statement and e-banking
 * search box already uses. Before this, one invoice read `CHF 3'200.00` on the pay page and
 * `CHF 3’200.00` on the PDF sent for the same amount.
 *
 * Decided: U+2019 everywhere: the frontend stops printing what its own Intl happens to emit
 * and instead agrees with the backend. A straight apostrophe never appears in Intl's numeric
 * output for any other reason (no letters, no words), so replacing every occurrence here is
 * safe for any locale, not just the Swiss ones — which is also why this lives in one place
 * rather than being repeated at each call site.
 */
export function toSwissGrouping(formatted: string): string {
  return formatted.replace(/'/g, '’')
}

/**
 * Hungarian money groups from four digits, agreeing with the backend (D32).
 *
 * Hungarian CLDR sets `minimumGroupingDigits = 2`, so Intl leaves a four-digit amount
 * ungrouped: `3500,00 CHF`. The JVM's `DecimalFormat` behind `MoneyFormatter` (PDFs, emails)
 * does not implement that CLDR field and prints `3 500,00 CHF` — same U+00A0 separator, same
 * trailing code, only the four-digit case differs. Andras decided (D32, 2026-09-29) that the
 * screen follows the document, so a Hungarian reader sees one figure on the pay page and on
 * the PDF beside it. `useGrouping: 'always'` is exactly that switch: it keeps Intl's own
 * Hungarian separator and ignores the two-digit minimum.
 *
 * EVERY locale since r15 B-F5. Hungarian (D32) and Polish (RB-43 D-14) were first; es-ES, it and
 * pt carry the same CLDR minimum (`minimumGroupingDigits = 2`) while the JVM groups from four
 * digits, so the screen printed "2541,00" beside a mail and PDF saying "2.541,00". One rule
 * is simpler than a list that grows a locale per sighting. The Swiss apostrophe stays
 * `toSwissGrouping`'s business. Returned as a spreadable options
 * fragment so the three money call sites (here and the two public pages' formatters) share
 * the one rule. A runtime too old for the string form reads it as truthy — i.e. today's
 * output — so the fallback is the shape we had, not a broken one.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept so call sites stay locale-shaped
export function moneyGroupingOptions(_locale: string): Pick<Intl.NumberFormatOptions, 'useGrouping'> {
  // The JVM behind the PDFs and mails groups from four digits in every locale, so the screen must too.
  return { useGrouping: 'always' }
}

/**
 * Money, as the ISO 4217 code plus the amount: "CHF 500.00", "EUR 500.00".
 *
 * `currencyDisplay: 'code'` rather than Intl's default symbol, deliberately. A studio
 * invoicing across borders sends documents where "$500" is three different amounts
 * (USD, CAD, AUD) and the symbol alone does not say which; the Swiss QR-bill and the
 * VAT return both name the currency by code for the same reason. Commit D swapped this
 * to symbols when it made formatting locale-aware, which was not the intent — the unit
 * tests have asserted codes throughout.
 *
 * Empty, null and NaN all format as zero through the SAME path as a real 0, so the
 * three are byte-identical. They used to take a hand-rolled `${code} 0.00` shortcut,
 * which differed from Intl's output by an ASCII space where Intl emits U+00A0 — and
 * under a fr-CH studio locale, where Intl puts the code AFTER the amount, differed in
 * word order too.
 *
 * NEGATIVE amounts get the minus in FRONT OF THE WHOLE THING — "-CHF 6’972.58", never
 * "CHF-6’972.58" (IN-61). Under a de-CH locale Intl glues its minus between the code and the
 * digits, which is where Finance ended up showing two shapes for the same kind of number on one
 * screen: the Net cash card read "CHF-6’972.58" while the Projection rows, which had always
 * hand-prefixed their own minus around a positive `formatCurrency`, read "-CHF 2’770.00". One of
 * the two had to move; this is the one that moves, because the hand-prefixed shape is the
 * readable one and because fixing it here fixes every negative in the app rather than the three
 * sites Finance happens to have. The magnitude still goes through Intl untouched, so grouping,
 * decimals and the fr-CH trailing-code word order are unchanged — only where the sign sits.
 * ASCII hyphen, not U+2212: these figures get copied into spreadsheets and mail.
 *
 * `n < 0` deliberately excludes negative zero, and `Math.abs` collapses it to a positive zero
 * before Intl sees it — otherwise a subtraction that lands on -0 (a projection month whose
 * outflow exactly matches its inflow) renders "CHF-0.00", which is the very shape this is
 * removing.
 */
export function formatCurrency(amount: number | string | null | undefined, currencyCode: string): string {
  const parsed = amount === null || amount === undefined || amount === ''
    ? 0
    : typeof amount === 'string' ? parseFloat(amount) : amount
  const n = Number.isNaN(parsed) ? 0 : parsed
  const locale = getStudioFormatLocale()
  const sign = n < 0 ? '-' : ''
  const magnitude = Math.abs(n)
  try {
    return toSwissGrouping(sign + magnitude.toLocaleString(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'code',
      ...moneyGroupingOptions(locale),
    }))
  } catch {
    // Intl throws on a malformed currency code. Same shape, so callers can't tell — which is
    // why the sign is applied out here too rather than only on the Intl path.
    return toSwissGrouping(`${sign}${currencyCode} ${magnitude.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2, ...moneyGroupingOptions(locale) })}`)
  }
}

/**
 * True when a BCP-47 tag's region subtag is CH — en-CH, de-CH, fr-CH, it-CH. Mirrors the
 * backend rule exactly: `DateFormatter`'s `"CH".equals(locale.getCountry())` (G4-R1 / M6).
 * Kept here, next to `formatDate`, rather than reimplemented at each call site — G2-F4/F9
 * found FOUR independent copies of this same CH-or-not decision (this file's own `formatDate`
 * was one of them) disagreeing with each other; one function is the fix.
 */
function isSwissRegion(locale: string): boolean {
  return locale.toUpperCase().endsWith('-CH')
}

/**
 * `dd.MM.yyyy`, built from the date's own local parts — never from `toLocaleDateString`.
 *
 * <b>This is OUR rule, not the locale's</b>, exactly like the three named preference
 * formats in `formatDateUserAware` below ("swiss" / "iso" / "us"), which are hand-built for
 * the same reason and whose Swiss arm now calls this. A Swiss document states its date
 * numerically because `DateFormatter.formatDate` on the backend says so (G4-R1), not
 * because some ICU build happens to agree — so asking `toLocaleDateString('en-CH', …)` for
 * it delegates a fixed contract to whatever CLDR data the runtime shipped with.
 *
 * That delegation is what broke: the `2-digit` token spec renders `30.06.2027` on
 * Chromium's bundled full-ICU and on Apple's WebKit, but `en-CH` is a region-only tag that
 * a reduced ICU resolves by falling back — to `en_150` (`30/06/2027`) or plain `en`
 * (`06/30/2027`). Both still render the "Due by" label next to them, so the page looks
 * fine and reads wrong. `invoice-pay-page-swiss-date.spec.ts` caught it on CI's Linux
 * WebKit while every local engine passed; a client's own device is the one place we cannot
 * choose the ICU build, which is precisely why the shape must not depend on it.
 *
 * Local parts, not UTC — same calendar day `toLocaleDateString` picked, so this is a
 * change of mechanism, not of meaning. With a `timeZone` the parts are that zone's (see
 * `datePartsIn`), exactly as `toLocaleDateString(…, { timeZone })` would pick them.
 */
function swissNumericDate(d: Date, timeZone?: string): string {
  const { year, month, day } = datePartsIn(d, timeZone)
  return `${pad2(day)}.${pad2(month)}.${year}`
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** `YYYY-MM-DD` exactly — a date with no time and no zone. */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Year, month (1–12) and day of `d` as a wall calendar in `timeZone` reads them — or the
 * browser's own zone when it is omitted, which is what `getDate()` & co. answer.
 *
 * The hand-built numeric formats (`swissNumericDate`, the "iso" / "us" preference arms)
 * cannot read `getDate()` once a zone is given: those getters are browser-local by
 * definition, so a 23:30 Zürich shoot read on a London laptop would print the day
 * before. 'en-CA' is a FORMAT selection, not a language — it is only asked for numeric
 * parts, and nothing it spells ever reaches the screen.
 */
function datePartsIn(d: Date, timeZone?: string): { year: number; month: number; day: number } {
  if (!timeZone) return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(d)
  const num = (type: string) => Number(parts.find(p => p.type === type)?.value)
  return { year: num('year'), month: num('month'), day: num('day') }
}

/** The optional zone every date/time formatter here accepts. Omitted → the viewer's zone. */
export interface ZoneOpts {
  timeZone?: string
}

/**
 * The IANA zone the viewer's browser is in ("Europe/London").
 *
 * Deliberately a function and NEVER cached in a module constant: tests and the screenshot
 * harness patch `Intl.DateTimeFormat.prototype.resolvedOptions` at runtime to play a
 * traveller, and a value frozen at import time would ignore them.
 */
export function getViewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** "GMT+2", "UTC-4", "GMT−4" — an offset spelled out instead of a name. */
const OFFSET_ABBREVIATION = /^(GMT|UTC)[+−-]/

/**
 * The short name of `timeZone` AT `date`: "CEST" in July and "CET" in January for
 * Europe/Zurich, "BST"/"GMT" for Europe/London, "EDT"/"EST" for America/New_York. At an
 * instant, because daylight saving decides which of the two applies.
 *
 * Two runtimes' worth of CLDR are consulted because neither alone names every zone a
 * client of a Swiss studio is likely to sit in: en-US answers "GMT+2" for Zürich (the very
 * spelling D7 rules out — "it must be clear that the displayed time is CET not GMT"), while
 * en-GB names Central and Western Europe and the UK but answers "GMT-4" for New York,
 * where en-US says "EDT". So: en-GB first; if that is only an offset, en-US; if that is an
 * offset too, the en-GB one. English on purpose, in every UI language — the backend's
 * emails print the same English abbreviations, and "MESZ" beside "CEST" for one booking
 * would be two names for one zone. These are FORMAT selections (a zone name, the same
 * token whichever language surrounds it), not a language leak.
 */
export function formatZoneAbbreviation(date: Date | string, timeZone: string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return ''
  const nameIn = (locale: string): string => {
    try {
      return new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
        .formatToParts(d).find(p => p.type === 'timeZoneName')?.value ?? ''
    } catch {
      return '' // an unknown zone id — callers render nothing rather than crash a page
    }
  }
  const gb = nameIn('en-GB')
  if (!OFFSET_ABBREVIATION.test(gb)) return gb
  const us = nameIn('en-US')
  return us && !OFFSET_ABBREVIATION.test(us) ? us : gb
}

/**
 * Day, abbreviated month and year in the STUDIO's locale: "15 Sept 2026" (en-US),
 * "15 sept. 2026" (fr-FR) — CH-region locales instead render numerically, `dd.MM.yyyy`
 * ("15.09.2026"), regardless of the document's language (G2-F4/F9, same rule as the
 * backend's `DateFormatter.formatDate`).
 *
 * <p>Until G2-F4/F9 this token spec (`month: 'short'`) could never produce Swiss numeric
 * output for ANY locale, en-CH included — the doc comment here used to give "15 Sept 2026"
 * as en-CH's own intended result, which is exactly the bug: a Swiss document states its
 * date numerically regardless of the document's language, same as the backend's
 * `DateFormatter.formatDate` already did before this fix (G4-R1). This was the one code
 * path shared between the client-facing invoice pay page and the studio-internal invoice
 * list/detail/Send-dialog screens — F4 and F9 of the same verifier run.
 *
 * These two carry no user preference at all — they are the plain internal-chrome date
 * formatters — so `getStudioFormatLocale()` is their only input. It used to be
 * `getActiveUiLocale()`, which is the split described on that function: the amount in the
 * next column was already formatting on the studio locale, so the two disagreed. Nothing
 * parses or sorts their output (audited across all 98 call sites); it is display-only.
 *
 * `locale` overrides that default with an explicit BCP-47 tag, and exists for the public
 * client-facing pages. There the reader is the CLIENT, and the client's tag may be one of
 * the six region-only CLIENT_LOCALES — but `usePublicLocale` has by then collapsed it to
 * its bundle on the global i18next instance, so the fallback would format a pt-PT client's
 * date the Brazilian way. Pass the full tag from the payload instead. Omit it for internal
 * chrome.
 *
 * `opts.timeZone` — the studio's `calendarTimezone` on a SCHEDULING screen, omitted
 * everywhere else (see the rule at the top of this file).
 */
export const formatDate = (iso: string | null | undefined, locale?: string | null, opts?: ZoneOpts): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const resolved = locale || getStudioFormatLocale()
  // A bare calendar day ("2026-10-20": an offer's valid-until, an invoice's due date, a date
  // answer) has no zone, and `new Date` reads it as UTC midnight — which a viewer west of
  // Greenwich would then print as the day BEFORE. Reading it back in UTC keeps the day it names.
  const timeZone = CALENDAR_DAY.test(iso) ? 'UTC' : opts?.timeZone
  // dd.MM.yyyy is a fixed contract, so it is built rather than looked up — see
  // swissNumericDate. Only the worded form below defers to the locale's own data.
  if (isSwissRegion(resolved)) return swissNumericDate(d, timeZone)
  return d.toLocaleDateString(resolved, { day: 'numeric', month: 'short', year: 'numeric', ...(timeZone ? { timeZone } : {}) })
}

export const formatDateShort = (iso: string | null | undefined, locale?: string | null, opts?: ZoneOpts): string => {
  if (!iso) return '—'
  const timeZone = opts?.timeZone
  return new Date(iso).toLocaleDateString(locale || getStudioFormatLocale(), { day: 'numeric', month: 'short', ...(timeZone ? { timeZone } : {}) })
}

/**
 * "5 min. ago", "vor 5 Min.", "hace 5 min" — and past a week, the absolute date.
 *
 * THE ONE relative-time formatter in the app. There used to be four hand-rolled copies of
 * the same ladder (`shootHelpers`, `ActivityTimeline`, `AppLayout`, `galleryManageHelpers`),
 * three of which emitted English literals — `just now`, `3d ago` — in every language, under
 * headings that were otherwise fully German. All four now delegate here.
 *
 * NO TRANSLATION KEYS, deliberately. `Intl.RelativeTimeFormat` is CLDR: it already ships
 * every one of these phrases for all six bundled locales and for the ones we do not bundle,
 * and it gets the plural rules right in languages whose rules a hand-written `_one`/`_other`
 * pair cannot express (Slavic few/many, Romance long-form agreement). This is the same
 * argument that makes the date formatters above take month and weekday names from Intl
 * rather than from the JSON bundles. `AppLayout` DID have keys — `layout:time.*`, four of
 * them in six files — and they are deleted with this change: a translated string that CLDR
 * already owns is a maintenance cost with no upside.
 *
 * `numeric: 'auto'` rather than `'always'`. It is what yields the idiom instead of the
 * arithmetic — "yesterday" / "gestern" / "hier" for one day, "now" / "jetzt" / "ahora" for
 * the under-a-minute case that used to be the literal `just now`. The 0-unit renderings
 * `auto` reserves for the current period ("this minute", "this hour") are unreachable here:
 * the ladder is ordered, so a 0-minute age has already returned from the seconds branch and
 * a 0-hour age from the minutes branch. Only `format(0, 'second')` is deliberate.
 *
 * `style: 'short'` rather than `'narrow'`, even though 'narrow' reproduces the old English
 * `3d ago` character for character. In fr-CH narrow collapses to a bare minus sign —
 * "-3 j", "-5 min" — which reads as arithmetic, not as a time in the past, to exactly the
 * non-technical user this app is for. 'short' stays compact in all six ("vor 5 Min.",
 * "il y a 5 min", "hace 5 min") and degrades to nothing worse than a slightly longer
 * English string.
 *
 * Past seven days it hands off to `formatDateUserAware`, so the member's date-format
 * preference still governs the absolute end of the scale — that boundary is unchanged from
 * all four originals. A FUTURE timestamp falls in the first branch and reads "now", which
 * is also what the originals did with it.
 *
 * THE ONE FORMATTER STILL ON `getActiveUiLocale()`, and deliberately so. Everything else
 * here moved to `getStudioFormatLocale()` on 2026-09-16 because a date and the amount
 * beside it must be shaped by one region. This one emits no shape: its whole output is a
 * SENTENCE — "5 min. ago", "vor 5 Min.", "il y a 5 min" — so it is chrome text like every
 * label around it and belongs in the reader's language. Rendering "vor 5 Min." to an
 * English reader because the studio writes to its clients in German would be a language
 * bug wearing a formatting bug's clothes. The >7d handoff below crosses back to the studio
 * locale on purpose: that branch really is a date.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'

  const rtf = new Intl.RelativeTimeFormat(getActiveUiLocale(), { numeric: 'auto', style: 'short' })
  const diffSec = Math.floor((Date.now() - then) / 1000)
  if (diffSec < 60) return rtf.format(0, 'second')
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return rtf.format(-diffMin, 'minute')
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return rtf.format(-diffH, 'hour')
  const diffD = Math.floor(diffH / 24)
  if (diffD < 7) return rtf.format(-diffD, 'day')
  return formatDateUserAware(iso)
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

/**
 * Picks the unit and the number to print for a file or archive size. DECIMAL (1 KB = 1000 B), the
 * convention Finder, Chrome and the download emails use. The backend's
 * `com.studioflow.core.api.ByteSizes.format` follows the same rule, and both are held to the same table of
 * cases (`src/test/resources/format-bytes-cases.json`), so a ZIP reads identically in the dialog and
 * in the email. Whole numbers below MB (a "1.5 KB" thumbnail is noise); one decimal from MB up. A
 * value that rounds up to 1000 moves to the next unit ("999.96 MB" is "1.0 GB", not "1000.0 MB").
 *
 * This is the ONE byte rule in the app (D54): file sizes, archive sizes, storage usage and plan
 * allowances alike. Plans are defined in decimal bytes too (4 TB = 4 * 10^12), so a plan prints as
 * "4.0 TB" here and on the usage bar, never "4.4 TB".
 */
function scaleDecimal(bytes: number): { value: number; unit: string; digits: number } {
  let i = 0
  while (i < BYTE_UNITS.length - 1 && bytes >= 1000 ** (i + 1)) i++
  let digits = i > 1 ? 1 : 0
  let value = bytes / 1000 ** i
  if (i > 0 && i < BYTE_UNITS.length - 1 && Math.round(value * 10 ** digits) / 10 ** digits >= 1000) {
    i++
    digits = i > 1 ? 1 : 0
    value = bytes / 1000 ** i
  }
  return { value, unit: BYTE_UNITS[i], digits }
}

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const { value, unit, digits } = scaleDecimal(bytes)
  // Through the user-aware number formatter so a Dutch/Swiss reader sees "5,0 GB" / "5.0 GB"
  // in their own grouping, not a hardcoded dot (r14 A-F2).
  return `${formatNumberUserAware(value, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${unit}`
}

/**
 * `formatBytes` with the digits of `locale` ("1,9 GB" in fr-CH, "1.9 GB" in en-US). For
 * client-facing pages that render in the CLIENT's locale rather than the signed-in member's
 * preferences; the unit letters stay the international ones, as in the emails.
 */
export const formatBytesForLocale = (bytes: number, locale: string | null | undefined): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const { value, unit, digits } = scaleDecimal(bytes)
  let number: string
  try {
    number = new Intl.NumberFormat(locale || undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })
      .format(value)
  } catch {
    // An unparseable tag must not blank the page — fall back to the neutral rendering.
    number = value.toFixed(digits)
  }
  return `${number} ${unit}`
}

// ── User-aware formatters ──────────────────────────────────────────────────────

interface CachedPreferences {
  dateFormat?: string | null
  timeFormat?: string | null
  numberFormat?: string | null
}

/**
 * Reads the current user's preferences from the React Query cache.
 * Returns null if not yet loaded (pre-login / first render).
 */
function getCurrentPreferences(): CachedPreferences | null {
  return (queryClient.getQueryData(queryKeys.settings.preferences) as CachedPreferences | undefined) ?? null
}

/**
 * Formats a date string or Date object according to the user's dateFormat preference.
 * - null / "auto" → the studio locale's own convention ("15 Sept 2026", "15. Sept. 2026")
 * - "swiss"       → DD.MM.YYYY
 * - "iso"         → YYYY-MM-DD
 * - "us"          → MM/DD/YYYY
 *
 * The three explicit formats are numeric patterns the user asked for by name, so they
 * are hand-built and stay identical in every language — that is the point of choosing
 * one. Only "auto" defers to the locale.
 *
 * `opts.timeZone` pins the calendar day to that zone in every arm — the studio's
 * `calendarTimezone` on a scheduling screen; omit it for "when did this happen".
 */
export function formatDateUserAware(date: Date | string | null | undefined, opts?: ZoneOpts): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '—'

  const prefs = getCurrentPreferences()
  const fmt = prefs?.dateFormat ?? null
  // A bare calendar day ("2026-01-01") names a day, not an instant: read it in UTC like formatDate
  // does, or a viewer west of Greenwich prints the day before.
  const timeZone = typeof date === 'string' && CALENDAR_DAY.test(date) ? 'UTC' : opts?.timeZone

  // One copy of dd.MM.yyyy in this file, shared with formatDate's CH-region arm.
  if (fmt === 'swiss') return swissNumericDate(d, timeZone)
  if (fmt === 'iso' || fmt === 'us') {
    const { year, month, day } = datePartsIn(d, timeZone)
    return fmt === 'iso'
      ? `${year}-${pad2(month)}-${pad2(day)}`
      : `${pad2(month)}/${pad2(day)}/${year}`
  }
  // auto or null → the studio's own region, same source as the money beside it
  return d.toLocaleDateString(getStudioFormatLocale(), { day: 'numeric', month: 'short', year: 'numeric', ...(timeZone ? { timeZone } : {}) })
}

/**
 * Formats a time string or Date object according to the user's timeFormat preference.
 * - null / "24h" → HH:MM (24-hour)
 * - "12h"        → h:MM AM/PM
 */
export function formatTimeUserAware(
  date: Date | string | null | undefined,
  opts?: ZoneOpts
): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '—'

  const prefs = getCurrentPreferences()
  const fmt = prefs?.timeFormat ?? null

  const localeOpts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...(opts?.timeZone ? { timeZone: opts.timeZone } : {}),
  }

  if (fmt === '12h') {
    // 'en-US' here is a FORMAT selection, not a language: it is the shortest way to get a
    // 12-hour clock for a user who explicitly asked for one. Nothing language-bearing
    // (month name, weekday) is emitted — hour and minute are 2-digit numerals.
    // eslint-disable-next-line no-restricted-syntax -- see above
    return d.toLocaleTimeString('en-US', { ...localeOpts, hour12: true })
  }
  // "24h" or null → 24-hour, in the studio's locale. `hourCycle: 'h23'` rather than
  // `hour12: false` because the latter resolves to h24 under some locale/ICU pairs and
  // renders midnight as "24:00"; h23 pins it to "00:00" everywhere.
  return d.toLocaleTimeString(getStudioFormatLocale(), { ...localeOpts, hourCycle: 'h23' })
}

/**
 * IN-114 A3 — the time slot on a redacted "Busy" block: "HH:MM – HH:MM" (both formatted
 * per the viewer's own time-format preference) or `allDayLabel` for an all-day entry.
 * Shared between the Calendar grid and the Shoots list so a Busy row reads identically
 * on both surfaces.
 */
export function formatBusyRange(
  startIso: string,
  endIso: string,
  allDay: boolean,
  allDayLabel: string,
  opts?: { timeZone?: string }
): string {
  if (allDay) return allDayLabel
  return `${formatTimeUserAware(startIso, opts)} – ${formatTimeUserAware(endIso, opts)}`
}

/**
 * Formats a number according to the user's numberFormat preference.
 * - null / "auto"     → the studio locale's own grouping (de-CH and en-CH → 1’234.56)
 * - "swiss"           → 1’234.56
 * - "european"        → 1.234,56
 * - "us"              → 1,234.56
 *
 * "auto" and "us" used to share the en-US branch, so "auto" was not auto — it was a
 * fourth hardcoded format that happened to equal one of the three named ones.
 */
export function formatNumberUserAware(
  n: number,
  opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  if (isNaN(n)) return '—'
  const fractionOpts = {
    minimumFractionDigits: opts?.minimumFractionDigits ?? 0,
    maximumFractionDigits: opts?.maximumFractionDigits ?? 2,
  }

  const prefs = getCurrentPreferences()
  const fmt = prefs?.numberFormat ?? null

  if (fmt === 'swiss') {
    // de-CH locale for the Swiss thousands grouping; toSwissGrouping (G4-R2) turns
    // Intl's own U+0027 apostrophe into the U+2019 the backend prints.
    return toSwissGrouping(n.toLocaleString('de-CH', fractionOpts))
  }
  if (fmt === 'european') {
    return n.toLocaleString('de-DE', fractionOpts)
  }
  if (fmt === 'us') {
    // 'en-US' here is a FORMAT selection, not a language: it is the grouping the user
    // picked by name ("1,234.56"), beside the 'de-CH' and 'de-DE' siblings above. Digits
    // and separators only — nothing language-bearing. G6 covers the bare toLocaleString
    // since 2026-09-07 because a date leak hid in that spelling; this is the one number
    // site it catches on the way past.
    // eslint-disable-next-line no-restricted-syntax -- see above
    return n.toLocaleString('en-US', fractionOpts)
  }
  // null / "auto" → the studio's own region, same source as formatCurrency
  return toSwissGrouping(n.toLocaleString(getStudioFormatLocale(), fractionOpts))
}
